import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'manage._base_domain_': [
      {
        srcPath: '/customization',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/stores/v3/customizations',
        destPath: '/v3/customizations',
      },
      {
        srcPath: '/stores/v3/bulk/customizations',
        destPath: '/v3/bulk/customizations',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/customization',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/customization',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/customization',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/customization',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/customization',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_stores_customizations-v-3';

/** Creates a customization. */
export function createCustomization(
  payload: object
): RequestOptionsFactory<any> {
  function __createCustomization({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'customization.createdDate' },
          { path: 'customization.updatedDate' },
          {
            path: 'customization.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'customization.choicesSettings.choices.image.focalPoint.x' },
          { path: 'customization.choicesSettings.choices.image.focalPoint.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.CreateCustomization',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'customization.createdDate' },
              { path: 'customization.updatedDate' },
              {
                path: 'customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCustomization;
}

/** Retrieves a customization. */
export function getCustomization(payload: object): RequestOptionsFactory<any> {
  function __getCustomization({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'GET' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.GetCustomization',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations/{customizationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'customization.createdDate' },
              { path: 'customization.updatedDate' },
              {
                path: 'customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCustomization;
}

/**
 * Updates a customization.
 *
 * Each time the customization is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the customization.
 * This ensures you're working with the latest customization and prevents unintended overwrites.
 */
export function updateCustomization(
  payload: object
): RequestOptionsFactory<any> {
  function __updateCustomization({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'customization.createdDate' },
          { path: 'customization.updatedDate' },
          {
            path: 'customization.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'customization.choicesSettings.choices.image.focalPoint.x' },
          { path: 'customization.choicesSettings.choices.image.focalPoint.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'PATCH' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.UpdateCustomization',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations/{customization.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'customization.createdDate' },
              { path: 'customization.updatedDate' },
              {
                path: 'customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCustomization;
}

/**
 * Deletes a customization.
 *
 * > **Notes:**
 * > + **Options** (customizations with `customizationType: PRODUCT_OPTION`) can't be deleted while assigned to any product because they affect product variants and inventory.
 * > + **Modifiers** can be deleted freely. When deleted, they are automatically removed from all products that reference them.
 */
export function deleteCustomization(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteCustomization({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'DELETE' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.DeleteCustomization',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations/{customizationId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCustomization;
}

/**
 * Retrieves a list of up to 100 customizations, given the provided filtering, sorting, and cursor paging.
 * Pass supported values to the `fields` array in the request to include those fields in the response.
 *
 *
 * Query Customizations runs with these defaults, which you can override:
 *
 * - `createdDate` is sorted in `DESC` order
 * - `cursorPaging.limit` is `100`
 *
 * To learn about working with _Query_ endpoints, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 */
export function queryCustomizations(
  payload: object
): RequestOptionsFactory<any> {
  function __queryCustomizations({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'GET' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.QueryCustomizations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations/query',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'customizations.createdDate' },
              { path: 'customizations.updatedDate' },
              {
                path: 'customizations.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'customizations.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'customizations.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'customizations.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
      fallback: [
        {
          method: 'POST' as any,
          url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
            protoPath: '/v3/customizations/query',
            data: payload,
            host,
          }),
          data: payload,
        },
      ],
    };

    return metadata;
  }

  return __queryCustomizations;
}

/** Creates multiple customizations. */
export function bulkCreateCustomizations(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkCreateCustomizations({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'customizations.createdDate' },
          { path: 'customizations.updatedDate' },
          {
            path: 'customizations.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'customizations.choicesSettings.choices.displayImage.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'customizations.choicesSettings.choices.image.focalPoint.x' },
          { path: 'customizations.choicesSettings.choices.image.focalPoint.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.BulkCreateCustomizations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/bulk/customizations/create',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.customization.createdDate' },
              { path: 'results.customization.updatedDate' },
              {
                path: 'results.customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'results.customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkCreateCustomizations;
}

/** Adds choices to a customization. */
export function addCustomizationChoices(
  payload: object
): RequestOptionsFactory<any> {
  function __addCustomizationChoices({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'choices.image.urlExpirationDate' },
          { path: 'choices.displayImage.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'choices.image.focalPoint.x' },
          { path: 'choices.image.focalPoint.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.AddCustomizationChoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations/{customizationId}/add-choices',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'customization.createdDate' },
              { path: 'customization.updatedDate' },
              {
                path: 'customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __addCustomizationChoices;
}

/**
 * Sets a customization's choices. Any and all existing choices will be overridden.
 *
 * > **Note:** A choice cannot be overridden if it is assigned to one or more products.
 */
export function setCustomizationChoices(
  payload: object
): RequestOptionsFactory<any> {
  function __setCustomizationChoices({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'choices.image.urlExpirationDate' },
          { path: 'choices.displayImage.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'choices.image.focalPoint.x' },
          { path: 'choices.image.focalPoint.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.SetCustomizationChoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations/{customizationId}/set-choices',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'customization.createdDate' },
              { path: 'customization.updatedDate' },
              {
                path: 'customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __setCustomizationChoices;
}

/**
 * Removes choices from a customization.
 *
 * > **Note:** A choice can't be removed if it's assigned to one or more product variants. This applies to option choices only; modifier choices can be removed freely.
 */
export function removeCustomizationChoices(
  payload: object
): RequestOptionsFactory<any> {
  function __removeCustomizationChoices({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.RemoveCustomizationChoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations/{customizationId}/remove-choices',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'customization.createdDate' },
              { path: 'customization.updatedDate' },
              {
                path: 'customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __removeCustomizationChoices;
}

/**
 * Updates properties of existing choices in a customization.
 * Choices are identified by their IDs. Only the fields specified in `fieldMask` are updated.
 *
 * Currently supports updating `primaryChoiceIds`.
 */
export function updateCustomizationChoices(
  payload: object
): RequestOptionsFactory<any> {
  function __updateCustomizationChoices({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'choices.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'choices.choice.image.urlExpirationDate' },
          { path: 'choices.choice.displayImage.urlExpirationDate' },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'choices.choice.image.focalPoint.x' },
          { path: 'choices.choice.image.focalPoint.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.UpdateCustomizationChoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations/{customizationId}/update-choices',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'customization.createdDate' },
              { path: 'customization.updatedDate' },
              {
                path: 'customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCustomizationChoices;
}

/**
 * Reorders a customization's choices. The new order is persisted and reflected in the customization's choices list.
 * Calling this endpoint sets `choicesSettings.sortOrder` to `MANUAL`.
 *
 * To control how choices are sorted in storefront filter panels, use [Update Customization](https://dev.wix.com/docs/api-reference/business-solutions/stores/catalog-v3/customizations-v3/update-customization) to set the `choicesSettings.sortOrder` field.
 */
export function reorderCustomizationChoices(
  payload: object
): RequestOptionsFactory<any> {
  function __reorderCustomizationChoices({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.ReorderCustomizationChoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/customizations/{customizationId}/reorder-choices',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'customization.createdDate' },
              { path: 'customization.updatedDate' },
              {
                path: 'customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __reorderCustomizationChoices;
}

/** Adds choices to multiple customizations. */
export function bulkAddCustomizationChoices(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkAddCustomizationChoices({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'customizationsChoices.choices.image.urlExpirationDate' },
          {
            path: 'customizationsChoices.choices.displayImage.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'customizationsChoices.choices.image.focalPoint.x' },
          { path: 'customizationsChoices.choices.image.focalPoint.y' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.BulkAddCustomizationChoices',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/bulk/customizations/add-choices',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.customization.createdDate' },
              { path: 'results.customization.updatedDate' },
              {
                path: 'results.customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'results.customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkAddCustomizationChoices;
}

/**
 * Updates multiple customizations.
 *
 * Each time the customization is updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the customization.
 * This ensures you're working with the latest customization and prevents unintended overwrites.
 */
export function bulkUpdateCustomizations(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateCustomizations({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'customizations.fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'customizations.customization.createdDate' },
          { path: 'customizations.customization.updatedDate' },
          {
            path: 'customizations.customization.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'customizations.customization.choicesSettings.choices.displayImage.urlExpirationDate',
          },
        ],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'customizations.customization.choicesSettings.choices.image.focalPoint.x',
          },
          {
            path: 'customizations.customization.choicesSettings.choices.image.focalPoint.y',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.customization',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.customization.v3.CustomizationService.BulkUpdateCustomizations',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCustomizationV3CustomizationServiceUrl({
        protoPath: '/v3/bulk/customizations/update',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.customization.createdDate' },
              { path: 'results.customization.updatedDate' },
              {
                path: 'results.customization.choicesSettings.choices.image.urlExpirationDate',
              },
              {
                path: 'results.customization.choicesSettings.choices.displayImage.urlExpirationDate',
              },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'results.customization.choicesSettings.choices.image.focalPoint.x',
              },
              {
                path: 'results.customization.choicesSettings.choices.image.focalPoint.y',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkUpdateCustomizations;
}
